// Units of measure: common presets (with the spellings shops use) and matching
// of free text to a business's units. Used by Settings, the product form and import.
import type { RowOf } from '@ims/data'

type UnitRow = RowOf<'units'>

export interface UnitPreset {
  symbol: string
  name: string
  allowsDecimal: boolean
  aliases: string[]
}

export const UNIT_PRESETS: UnitPreset[] = [
  {
    symbol: 'pcs',
    name: 'Pieces',
    allowsDecimal: false,
    aliases: [
      'pc',
      'piece',
      'pieces',
      'nos',
      'no',
      'each',
      'ea',
      'unit',
      'units',
      'wata',
      'वटा',
      'थान',
      'गोटा',
    ],
  },
  {
    symbol: 'kg',
    name: 'Kilogram',
    allowsDecimal: true,
    aliases: ['kgs', 'kilo', 'kilos', 'kilogram', 'kilograms', 'केजी', 'किलो'],
  },
  {
    symbol: 'g',
    name: 'Gram',
    allowsDecimal: true,
    aliases: ['gm', 'gms', 'gram', 'grams', 'gr', 'ग्राम'],
  },
  {
    symbol: 'l',
    name: 'Litre',
    allowsDecimal: true,
    aliases: ['ltr', 'ltrs', 'lt', 'litre', 'litres', 'liter', 'liters', 'लिटर'],
  },
  {
    symbol: 'ml',
    name: 'Millilitre',
    allowsDecimal: true,
    aliases: ['millilitre', 'milliliter', 'mls'],
  },
  {
    symbol: 'm',
    name: 'Metre',
    allowsDecimal: true,
    aliases: ['mtr', 'mtrs', 'metre', 'meter', 'metres', 'meters', 'मिटर'],
  },
  { symbol: 'dozen', name: 'Dozen', allowsDecimal: false, aliases: ['dz', 'doz', 'dzn', 'दर्जन'] },
  { symbol: 'box', name: 'Box', allowsDecimal: false, aliases: ['boxes', 'bx', 'बाकस'] },
  {
    symbol: 'pack',
    name: 'Pack',
    allowsDecimal: false,
    aliases: ['packs', 'pkt', 'pkts', 'packet', 'packets', 'प्याकेट'],
  },
  { symbol: 'pair', name: 'Pair', allowsDecimal: false, aliases: ['pairs', 'pr', 'जोडी'] },
  { symbol: 'set', name: 'Set', allowsDecimal: false, aliases: ['sets'] },
  { symbol: 'bottle', name: 'Bottle', allowsDecimal: false, aliases: ['bottles', 'btl', 'बोतल'] },
  { symbol: 'bag', name: 'Bag', allowsDecimal: false, aliases: ['bags', 'sack', 'bora', 'बोरा'] },
  { symbol: 'roll', name: 'Roll', allowsDecimal: false, aliases: ['rolls'] },
  { symbol: 'carton', name: 'Carton', allowsDecimal: false, aliases: ['cartons', 'ctn', 'ctns'] },
  { symbol: 'tola', name: 'Tola', allowsDecimal: true, aliases: ['tolas', 'तोला'] },
  // Labour and services (workshops): sold by time, by the job or by the visit.
  { symbol: 'hr', name: 'Hour', allowsDecimal: true, aliases: ['hrs', 'hour', 'hours', 'घण्टा'] },
  { symbol: 'job', name: 'Job', allowsDecimal: false, aliases: ['jobs', 'service', 'काम'] },
  { symbol: 'visit', name: 'Visit', allowsDecimal: false, aliases: ['visits', 'trip', 'trips'] },
  {
    symbol: 'km',
    name: 'Kilometre',
    allowsDecimal: true,
    aliases: ['kms', 'kilometre', 'kilometer', 'kilometres', 'kilometers', 'किमी'],
  },
  // Lengths and areas still sold the old way: pipe by the foot, glass by the square foot.
  { symbol: 'ft', name: 'Foot', allowsDecimal: true, aliases: ['feet', 'foot', 'फिट'] },
  { symbol: 'inch', name: 'Inch', allowsDecimal: true, aliases: ['in', 'inches', 'इन्च'] },
  {
    symbol: 'sqft',
    name: 'Square foot',
    allowsDecimal: true,
    aliases: ['sq ft', 'sq.ft', 'square feet', 'sft', 'वर्ग फिट'],
  },
  {
    symbol: 'mm',
    name: 'Millimetre',
    allowsDecimal: true,
    aliases: ['millimetre', 'millimeter', 'millimetres', 'millimeters'],
  },
  { symbol: 'can', name: 'Can', allowsDecimal: false, aliases: ['cans', 'tin', 'tins'] },
  { symbol: 'tube', name: 'Tube', allowsDecimal: false, aliases: ['tubes'] },
  { symbol: 'drum', name: 'Drum', allowsDecimal: false, aliases: ['drums', 'barrel', 'barrels'] },
  { symbol: 'coil', name: 'Coil', allowsDecimal: false, aliases: ['coils'] },
]

const key = (value: string) => value.trim().toLowerCase().replace(/\.$/, '')

/** The preset a spelling belongs to ("pkt" → Pack), or null. */
export function presetFor(raw: string): UnitPreset | null {
  const k = key(raw)
  return (
    UNIT_PRESETS.find(
      (p) => p.symbol === k || p.name.toLowerCase() === k || p.aliases.includes(k),
    ) ?? null
  )
}

/**
 * The business's unit for some text: its symbol or name, else any unit the
 * text's preset stands for ("ltr" finds the business's "l").
 */
export function findUnit(units: UnitRow[], raw: string): UnitRow | null {
  const k = key(raw)
  if (!k) return null
  const direct = units.find((u) => key(u.symbol) === k || key(u.name) === k)
  if (direct) return direct
  const preset = presetFor(k)
  if (!preset) return null
  const spellings = [preset.symbol, preset.name.toLowerCase(), ...preset.aliases]
  return (
    units.find((u) => spellings.includes(key(u.symbol)) || spellings.includes(key(u.name))) ?? null
  )
}

export interface NewUnitSpec {
  symbol: string
  name: string
  allowsDecimal: boolean
}

/** What to create for an unknown unit: a known preset, else the text itself. */
export function unitForNew(raw: string): NewUnitSpec {
  const preset = presetFor(raw)
  if (preset) {
    return { symbol: preset.symbol, name: preset.name, allowsDecimal: preset.allowsDecimal }
  }
  const text = raw.trim().replace(/\s+/g, ' ')
  return {
    symbol: text.toLowerCase().slice(0, 16),
    name: text.charAt(0).toUpperCase() + text.slice(1),
    allowsDecimal: false,
  }
}

/** Presets the business doesn't have yet (for quick-add). */
export function missingPresets(units: UnitRow[]): UnitPreset[] {
  return UNIT_PRESETS.filter((p) => !findUnit(units, p.symbol))
}
